// CalendarItemQuickView — right-hand quick view of a single calendar item. Loads
// the ACTUAL source record (incident, document, visit, action, …) and shows a
// compact detail card with a "Back to calendar" action — so users inspect the
// real item without leaving the calendar. Native events get inline edit/delete.
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, ExternalLink, Download, Pencil, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";

const STATUS_TONE = { Scheduled: "blue", Completed: "green", Done: "green", Cancelled: "neutral",
    Open: "amber", "In progress": "blue", Blocked: "red", Submitted: "blue", Closed: "neutral",
    Issued: "blue", Approved: "green", Reported: "amber", Investigating: "blue", Resolved: "green",
    "Action required": "amber", Critical: "red", Satisfactory: "green", "Due soon": "amber", Overdue: "red" };

const fmtDate = (v) => {
    if (!v) return null;
    const d = new Date(v.length <= 10 ? v + "T00:00:00" : v);
    return isNaN(d) ? v : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtDateTime = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d) ? v : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const clean = (arr) => arr.filter(([, v]) => v !== null && v !== undefined && v !== "");

// Per-source: how to fetch + render the record.
const SOURCE_CONFIG = {
    incident: {
        endpoint: (id) => `/incidents/${id}`,
        title: (r) => (r.reference ? `${r.reference} · ${r.incident_type}` : r.incident_type || "Incident"),
        pills: (r) => [r.status, r.severity, r.riddor_category && r.riddor_category !== "Not reportable" ? "RIDDOR" : null],
        fields: (r) => clean([["Occurred", fmtDateTime(r.occurred_at)], ["Location", r.location],
            ["Reported by", r.reported_by_name], ["RIDDOR category", r.riddor_category], ["Description", r.description]]),
        open: (r) => ({ route: "/app/incidents", label: "Open in Accidents & Incidents" }),
        quickAction: { label: "Close incident", patch: { status: "Closed" }, done: (r) => r.status === "Closed" },
    },
    action: {
        endpoint: (id) => `/actions/${id}`,
        title: (r) => r.title || "Action",
        pills: (r) => [r.status, r.priority],
        fields: (r) => clean([["Due", fmtDate(r.due_date)], ["Assignee", r.assignee_name],
            ["Source", r.source], ["Description", r.description]]),
        open: () => ({ route: "/app/actions", label: "Open in Actions" }),
        quickAction: { label: "Mark complete", patch: { status: "Complete" }, done: (r) => r.status === "Complete" },
    },
    document: {
        endpoint: (id) => `/documents/${id}`,
        title: (r) => r.title || "Document",
        pills: (r) => [r.status, r.category, `v${r.version_number}`],
        fields: (r) => clean([["Review date", fmtDate(r.review_date)], ["Expiry date", fmtDate(r.expiry_date)],
            ["Owner", r.owner_name], ["File", r.filename], ["Description", r.description]]),
        open: (r) => ({ route: "/app/documents", label: "Open in Document Library" }),
        download: (r) => `/documents/${r.id}/download`,
    },
    site_visit: {
        endpoint: (id) => `/visits/${id}`,
        title: (r) => r.title || r.visit_type || "Site visit",
        pills: (r) => [r.status, r.visit_type],
        fields: (r) => clean([["Scheduled", fmtDate(r.scheduled_date)], ["Time", r.scheduled_time],
            ["Assignee", r.assignee_name], ["Recurrence", r.recurrence !== "None" ? r.recurrence : null], ["Notes", r.notes]]),
        open: () => ({ route: "/app/schedule", label: "Open in Site Visits & Schedule" }),
        quickAction: { label: "Mark completed", patch: { status: "Completed" }, done: (r) => r.status === "Completed" || r.status === "Cancelled" },
    },
    inspection: {
        endpoint: (id) => `/forms/instances/${id}`,
        title: (r) => r.title || r.template_name || "Inspection",
        pills: (r) => [r.status, r.template_code],
        fields: (r) => clean([["Due", fmtDate(r.due_date)], ["Assignee", r.assignee_name],
            ["Submitted", fmtDateTime(r.submitted_at)], ["Notes", r.notes]]),
        open: (r) => ({ route: `/app/inspections/${r.id}`, label: "Open full inspection" }),
    },
    resident: {
        endpoint: (id) => `/residents/${id}`,
        title: (r) => (r.reference ? `${r.reference} · ${r.concern_type}` : r.concern_type || "Resident concern"),
        pills: (r) => [r.status, r.priority],
        fields: (r) => clean([["Target response", fmtDate(r.target_response_date)], ["Raised by", r.anonymous ? "Anonymous" : r.raised_by_name],
            ["Unit", r.raised_by_unit], ["Description", r.description], ["Response", r.response_summary]]),
        open: () => ({ route: "/app/residents", label: "Open in Resident Engagement" }),
        quickAction: { label: "Mark resolved", patch: { status: "Resolved" }, done: (r) => r.status === "Resolved" || r.status === "Closed" },
    },
    fire: {
        endpoint: (id) => `/fire-safety/${id}`,
        title: (r) => r.measure || "Fire safety measure",
        pills: (r) => [r.condition, r.measure_type, r.fra_rating],
        fields: (r) => clean([["Next due", fmtDate(r.next_due)], ["Last inspected", fmtDate(r.last_inspected)],
            ["FRA date", fmtDate(r.fra_date)], ["Responsible", r.responsible], ["Findings", r.significant_findings]]),
        open: () => ({ route: "/app/fire", label: "Open in Fire Safety" }),
        quickAction: { label: "Mark satisfactory", patch: { condition: "Satisfactory" }, done: (r) => r.condition === "Satisfactory" },
    },
    bsa: {
        endpoint: (id) => `/bsa/${id}`,
        title: (r) => r.requirement || "BSA requirement",
        pills: (r) => [r.status, r.category],
        fields: (r) => clean([["Due", fmtDate(r.due_date)], ["Last completed", fmtDate(r.last_completed)],
            ["Frequency", r.frequency], ["Accountable", r.accountable_person], ["Notes", r.notes]]),
        open: () => ({ route: "/app/bsa", label: "Open in BSA Register" }),
        quickAction: { label: "Mark compliant", patch: { status: "Compliant" }, done: (r) => r.status === "Compliant" },
    },
    issue: {
        endpoint: (id) => `/issues/${id}`,
        title: (r) => r.title || "Issued document",
        pills: (r) => [r.status, r.issue_type, r.completion_mode],
        fields: (r) => clean([["Due", fmtDate(r.due_date)], ["Recipients", (r.recipients || []).length || null],
            ["Instructions", r.instructions]]),
        open: () => ({ route: "/app/documents", label: "Open in Document Library" }),
    },
    project: {
        endpoint: (id) => `/projects/${id}`,
        title: (r) => r.name || "Project",
        pills: (r) => [r.status, r.reference],
        fields: (r) => clean([["Start", fmtDate(r.start_date)], ["Target", fmtDate(r.target_date)],
            ["Completed", fmtDate(r.completed_at)], ["Lead", r.lead_consultant], ["Type", r.project_type]]),
        open: (r) => ({ route: `/app/projects/${r.id}`, label: "Open project" }),
    },
};

export default function CalendarItemQuickView({ item, onBack, onEditNative, onDeleteNative, navigate, canAct = false, onChanged }) {
    const isNative = item.source_module === "event";
    const cfg = SOURCE_CONFIG[item.source_module];
    const [rec, setRec] = useState(null);
    const [loading, setLoading] = useState(!isNative);
    const [error, setError] = useState(null);
    const [acting, setActing] = useState(false);

    useEffect(() => {
        if (isNative || !cfg) { setLoading(false); return; }
        let alive = true;
        setLoading(true); setError(null);
        api.get(cfg.endpoint(item.source_id))
            .then((r) => { if (alive) setRec(r.data); })
            .catch((e) => { if (alive) setError(formatApiError(e)); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [item.source_id, item.source_module]);

    async function runQuickAction() {
        if (!cfg?.quickAction || !rec) return;
        setActing(true);
        try {
            const { data } = await api.patch(cfg.endpoint(rec.id), cfg.quickAction.patch);
            setRec(data);
            toast.success(`${cfg.title(data)} — updated`);
            onChanged && onChanged();
        } catch (e) { toast.error(formatApiError(e)); } finally { setActing(false); }
    }

    const colour = item.colour;
    const Header = (
        <div className="flex items-start justify-between gap-2 mb-3">
            <button data-testid="calendar-quickview-back" onClick={onBack}
                className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F]">
                <ArrowLeft size={14} /> Back to calendar
            </button>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white" style={{ background: colour }}>
                {item.event_type}
            </span>
        </div>
    );

    return (
        <div className="rounded-2xl bg-white border border-[#E5E0DA] p-5" data-testid="calendar-quickview">
            {Header}

            {isNative ? (
                <NativeBody item={item} onEdit={onEditNative} onDelete={onDeleteNative} />
            ) : loading ? (
                <div className="py-10 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</div>
            ) : error ? (
                <div className="py-6 text-center text-[13px] text-red-600" data-testid="calendar-quickview-error">{error}</div>
            ) : rec && cfg ? (
                <>
                    <h3 className="text-base font-bold text-[#1F1F1F] leading-snug">{cfg.title(rec)}</h3>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {cfg.pills(rec).filter(Boolean).map((p, i) => <Pill key={i} tone={STATUS_TONE[p] || "neutral"}>{p}</Pill>)}
                    </div>
                    <dl className="mt-4 space-y-2.5" data-testid="calendar-quickview-fields">
                        {cfg.fields(rec).map(([label, value]) => (
                            <div key={label} className="grid grid-cols-3 gap-2">
                                <dt className="text-[11px] font-bold uppercase tracking-wide text-[#9A968F] col-span-1">{label}</dt>
                                <dd className="text-[12.5px] text-[#3e4b63] col-span-2 whitespace-pre-wrap">{value}</dd>
                            </div>
                        ))}
                    </dl>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {canAct && cfg.quickAction && !cfg.quickAction.done(rec) && (
                            <button data-testid="calendar-quickview-quickaction" onClick={runQuickAction} disabled={acting}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold rounded-lg text-white disabled:opacity-50"
                                style={{ background: "#3F7D3A" }}>
                                {acting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} {cfg.quickAction.label}
                            </button>
                        )}
                        {cfg.download && (
                            <a data-testid="calendar-quickview-download" href={api.defaults.baseURL + cfg.download(rec)} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold rounded-lg border border-[#E5E0DA] text-[#6F6A64] hover:bg-[#F5F3F0]">
                                <Download size={13} /> Download
                            </a>
                        )}
                        {cfg.open && (
                            <button data-testid="calendar-quickview-open" onClick={() => navigate(cfg.open(rec).route)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold rounded-lg border border-[#E5E0DA] text-[#3e4b63] hover:bg-[#F5F3F0]">
                                <ExternalLink size={13} /> {cfg.open(rec).label}
                            </button>
                        )}
                    </div>
                </>
            ) : (
                <div className="py-6 text-center text-[13px] text-[#9A968F]">No preview available for this item.</div>
            )}
        </div>
    );
}

function NativeBody({ item, onEdit, onDelete }) {
    return (
        <>
            <h3 className="text-base font-bold text-[#1F1F1F] leading-snug">{item.title}</h3>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {item.status && <Pill tone={STATUS_TONE[item.status] || "neutral"}>{item.status}</Pill>}
                <Pill tone="neutral">{item.date}</Pill>
                {item.time && <Pill tone="neutral">{item.time}</Pill>}
            </div>
            <p className="mt-4 text-[12px] text-[#9A968F]">Ad-hoc calendar event.{(onEdit || onDelete) ? " Use Edit to change details or Delete to remove it." : ""}</p>
            {(onEdit || onDelete) && (
                <div className="mt-5 flex gap-2">
                    {onEdit && (
                        <button data-testid="calendar-quickview-edit" onClick={onEdit}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold rounded-lg text-white"
                            style={{ background: "var(--brand-accent, #C75A1B)" }}><Pencil size={13} /> Edit</button>
                    )}
                    {onDelete && (
                        <button data-testid="calendar-quickview-delete" onClick={onDelete}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold rounded-lg border border-[#E5E0DA] text-red-600 hover:bg-red-50">
                            <Trash2 size={13} /> Delete</button>
                    )}
                </div>
            )}
        </>
    );
}
